import React from "react";
import styled from "styled-components";
import { FaLinkedin, FaEnvelope, FaGithub, FaDownload } from "react-icons/fa";
import RevealOnScroll from "../../utils/RevealOnScroll";
import TextShift from "../../utils/TextShift";
import ExplodingText from '../../utils/ExplodingText';
import Res from "./../../images/Resume.pdf";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
`;

const DownloadButton = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-size: 16px;
  height: 40px;
  font-weight: 600;
  text-align: center;
  width: 200px;
  text-decoration: none;
  border: none;
  background-size: 300% 100%;
  border-radius: 50px;
  font-family: "maitree", sans-serif;
  transition: all 0.4s ease-in-out;
  background-image: linear-gradient(
    to right,
    rgba(114, 114, 114, 0),
    rgba(106, 106, 107, 0),
    rgba(98, 99, 101, 0),
    rgba(89, 92, 94, 0),
    rgba(80, 85, 87, 0),
    #505557,
    #4a4f51,
    #44494a,
    #3e4344,
    #393c3e,
    #353637,
    #303031,
    #2a2a2a
  );
  box-shadow: 0 4px 15px 0 rgb(242, 242, 242, 0.2);

  &:hover {
    background-position: 100% 0;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 180px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-bottom: 1rem;
`;

const ResumeContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  color: white;
  font-family: "Asap", sans-serif;
  border-radius: 10px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContactInfo = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;
  color: #b2b2b2;
  font-family: "Asap";

  a {
    color: #b2b2b2;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: white;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
  }
`;

const Section = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: white;
  border-bottom: 2px solid #444;
  padding-bottom: 0.5rem;
  margin-bottom: 1.5rem;
  font-family: "moret-variable";
`;

const SectionContent = styled.div`
  color: #b2b2b2;
  line-height: 1.6;
  font-family: "Asap";
`;

const ExperienceItem = styled.div`
  margin-bottom: 2rem;
`;

const ExperienceTitle = styled.h3`
  font-size: 1.4rem;
  color: white;
  margin: 0;
  font-family: "moret-variable";
`;

const ExperienceSubtitle = styled.p`
  color: #b2b2b2;
  margin: 0.5rem 0;
  font-family: "Asap";
`;

const ExperienceDescription = styled.ul`
  margin: 1rem 0 0 1.5rem;
  padding: 0;
`;

const ExperienceDescriptionItem = styled.li`
  margin-bottom: 0.8rem;
  color: #b2b2b2;
  font-family: "Asap";
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Skill = styled.span`
  background: #2a2a2a;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 1rem;
  font-family: "Asap";
  transition: all 0.3s ease;

  &:hover {
    background: #444;
    transform: translateY(-2px);
  }
`;

const SocialLinksContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 3rem;
`;

const SocialLink = styled.a`
  color: white;
  font-size: 1.8rem;
  transition: all 0.3s ease;

  &:hover {
    color: #b2b2b2;
    transform: translateY(-2px);
  }
`;

const Resume = () => {
  return (
    <PageContainer>
      <ResumeContainer>
        <Header>
        <ButtonContainer>
              <DownloadButton href={Res} download="NoahKornberg_Resume.pdf">
                <FaDownload /> Download Resume
              </DownloadButton>
            </ButtonContainer>
          <RevealOnScroll>
            <TextShift
              text="NOAH KORNBERG"
              maxWeight={800}
              minWeight={300}
              fontSize="4rem"
              mobileFontSize="3rem"
              adjacentWeightChange={100}
              fontFamily="moret-variable"
            />
          </RevealOnScroll>
          
          <RevealOnScroll delay={50}>
            <ContactInfo>
              <span>(647) 907-6548</span>
              <a href="mailto:noahkornberg@gmail.com">noahkornberg@gmail.com</a>
              <a href="https://www.linkedin.com/in/noah-kornberg-463149231" target="_blank" rel="noopener noreferrer">
                linkedin.com/in/noah-kornberg
              </a>
            </ContactInfo>
          </RevealOnScroll>
          <RevealOnScroll delay={100}>
          
          </RevealOnScroll>
        </Header>

        <RevealOnScroll delay={150}>
          <Section>
            <SectionTitle>About Me</SectionTitle>
            <SectionContent>
              <ExplodingText text="Experienced software developer skilled in blending artistic creativity with technical expertise. I excel in building full-stack applications, managing complex data systems, and developing AI-driven solutions. With a strong background in automation, specializing in creating workflows and pipelines that streamline processes, reduce inefficiencies, and enhance productivity. My work bridges the gap between innovative design and robust technical performance, delivering impactful solutions tailored to diverse needs." />
            </SectionContent>
          </Section>
        </RevealOnScroll>

        <RevealOnScroll delay={200}>
          <Section>
            <SectionTitle>Education</SectionTitle>
            <SectionContent>
              <ExperienceItem>
                <ExperienceTitle>University of Concordia</ExperienceTitle>
                <ExperienceSubtitle>
                  Honours Computational Arts | Montreal, Quebec | September 2025
                </ExperienceSubtitle>
              </ExperienceItem>
            </SectionContent>
          </Section>
        </RevealOnScroll>

        <RevealOnScroll delay={250}>
          <Section>
            <SectionTitle>Experience</SectionTitle>
            <SectionContent>
            <ExperienceItem>
                <ExperienceTitle>Freelance Junior Designer</ExperienceTitle>
                <ExperienceSubtitle>Remote | June 2023 – Present</ExperienceSubtitle>
                <ExperienceDescription>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Created and managed sponsored content campaigns, securing partnerships with brands including Arc’teryx, Twisted
Tea, Red Bull, Burton, and Gutsy to enhance brand visibility and engagement." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Developed and executed content strategies aligned with brand guidelines and marketing objectives, ensuring
high-performance results." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Negotiated contracts, maintained strong sponsor relationships, and oversaw campaign execution to meet brand and
business goals." />
                  </ExperienceDescriptionItem>
  
                </ExperienceDescription>
              </ExperienceItem>
              <ExperienceItem>
                <ExperienceTitle>Concordia Ski and Snowboard Club</ExperienceTitle>
                <ExperienceSubtitle>President | Montreal | September 2022 – Present</ExperienceSubtitle>
                <ExperienceDescription>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Automated the ticket lifecycle to increase sales efficiency and improve customer satisfaction, ensuring a seamless purchase experience." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Developed a full-stack eCommerce platform." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Organized and executed multiple events averaging over 200+ attendees." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Collaborated with high-value sponsors (Arc'teryx, Twisted Tea, Redbull) to develop media strategies and create content that boosted online presence." />
                  </ExperienceDescriptionItem>
                </ExperienceDescription>
              </ExperienceItem>

              <ExperienceItem>
                <ExperienceTitle>Concordia University</ExperienceTitle>
                <ExperienceSubtitle>Teaching Assistant | Montreal | January 2024 – Present</ExperienceSubtitle>
                <ExperienceDescription>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Led workshops to teach students software skills, including JavaScript, TouchDesigner, and the Adobe Suite." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Developed a repository of handcrafted scripts and components to facilitate student education and enhance learning outcomes." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Supported students in mastering technical concepts and tools, fostering a deeper understanding of course material." />
                  </ExperienceDescriptionItem>
                </ExperienceDescription>
              </ExperienceItem>

              <ExperienceItem>
                <ExperienceTitle>WEFX</ExperienceTitle>
                <ExperienceSubtitle>Jr. Editor / Pipeline Automation Intern | Remote | April 2023 – June 2023</ExperienceSubtitle>
                <ExperienceDescription>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Created Python scripts to automate file conversion and data extraction, streamlined pipeline workflows, and eliminated manual tasks to improve efficiency." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Enhanced efficiency and user experience by integrating Python scripts into a custom-developed YAML tool." />
                  </ExperienceDescriptionItem>
                  <ExperienceDescriptionItem>
                    <ExplodingText text="Maintained consistent visual quality by creating and managing LUTs for video clips." />
                  </ExperienceDescriptionItem>
                </ExperienceDescription>
              </ExperienceItem>

           
            </SectionContent>
          </Section>
        </RevealOnScroll>

        <RevealOnScroll delay={300}>
          <Section>
            <SectionTitle>Technical Skills</SectionTitle>
            <SectionContent>
              <SkillsList>
                <Skill>Python</Skill>
                <Skill>Java</Skill>
                <Skill>C#</Skill>
                <Skill>YAML</Skill>
                <Skill>JavaScript</Skill>
                <Skill>HTML</Skill>
                <Skill>CSS</Skill>
                <Skill>SQL</Skill>
                <Skill>PHP</Skill>
                <Skill>React.js</Skill>
                <Skill>Bootstrap</Skill>
                <Skill>jQuery</Skill>
                <Skill>Node.js</Skill>
                <Skill>Next.js</Skill>
                <Skill>Git</Skill>
                <Skill>Docker</Skill>
                <Skill>openAI</Skill>
                <Skill>Gcloud</Skill>
              </SkillsList>
            </SectionContent>
          </Section>
        </RevealOnScroll>

        <RevealOnScroll delay={350}>
          <Section>
            <SectionTitle>Exhibitions & Interests</SectionTitle>
            <SectionContent>
              <p>
                <strong>Art Exhibitions:</strong> 
                <ExplodingText text="DASA year-end show 2024, SandBox collective 2024" />
              </p>
              <p>
                <strong>Interests:</strong> 
                <ExplodingText text="Snowboarding (Level 1 Certified Instructor), Reading, Fashion, Digital Art" />
              </p>
            </SectionContent>
          </Section>
        </RevealOnScroll>

        <RevealOnScroll delay={400}>
          <SocialLinksContainer>
            <SocialLink 
              href="https://www.linkedin.com/in/noah-kornberg-463149231" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <FaLinkedin />
            </SocialLink>
            <SocialLink href="mailto:noahkornberg@gmail.com">
              <FaEnvelope />
            </SocialLink>
            <SocialLink 
              href="https://github.com/PinknMatter" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <FaGithub />
            </SocialLink>
          </SocialLinksContainer>
        </RevealOnScroll>
      </ResumeContainer>
    </PageContainer>
  );
};

export default Resume;